import { useState } from 'react';
import { Mail, CheckCircle } from 'lucide-react';

const API = import.meta.env.VITE_API_URL?.replace('/v1', '') || 'http://64.23.240.10/api';

interface NewsletterSignupProps {
  source?: string;
  compact?: boolean;
}

export default function NewsletterSignup({ source = 'website', compact = false }: NewsletterSignupProps) {
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    setStatus('loading');
    try {
      const res = await fetch(`${API}/newsletter/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, firstName, source })
      });
      const data = await res.json();
      if (res.ok) {
        setStatus('success');
        setMessage('You\'re subscribed! Check your inbox for a welcome email.');
      } else {
        setStatus('error');
        setMessage(data.error === 'Already subscribed' ? 'You\'re already subscribed!' : 'Something went wrong. Please try again.');
      }
    } catch {
      setStatus('error');
      setMessage('Something went wrong. Please try again.');
    }
  };

  if (status === 'success') return (
    <div className={`${compact ? 'p-4' : 'p-8'} bg-green-400/10 border border-green-400/30 rounded-xl text-center`}>
      <CheckCircle className="w-10 h-10 text-green-400 mx-auto mb-3" />
      <p className="text-green-400 font-semibold">{message}</p>
    </div>
  );

  if (compact) return (
    <form onSubmit={handleSubmit} className="flex gap-2">
      <input
        type="email"
        value={email}
        onChange={e => setEmail(e.target.value)}
        placeholder="Enter your email"
        required
        className="flex-1 bg-navy-dark border border-sky-blue/30 rounded-lg px-4 py-2.5 text-cream placeholder-sky-blue/50 focus:outline-none focus:border-neon-red transition-colors text-sm"
      />
      <button
        type="submit"
        disabled={status === 'loading'}
        className="btn-neon px-4 py-2.5 text-sm whitespace-nowrap disabled:opacity-50"
      >
        {status === 'loading' ? '...' : 'Subscribe'}
      </button>
    </form>
  );

  return (
    <div className="card-premium">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-12 h-12 bg-gradient-cta rounded-xl flex items-center justify-center shadow-neon">
          <Mail className="w-6 h-6 text-white" />
        </div>
        <div>
          <h3 className="text-xl font-bold text-cream">Stay updated</h3>
          <p className="text-sky-blue text-sm">Weekly staking rates and DeFi guides</p>
        </div>
      </div>
      <form onSubmit={handleSubmit} className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <input
            type="text"
            value={firstName}
            onChange={e => setFirstName(e.target.value)}
            placeholder="First name (optional)"
            className="bg-navy-dark border border-sky-blue/30 rounded-lg px-4 py-2.5 text-cream placeholder-sky-blue/50 focus:outline-none focus:border-neon-red transition-colors text-sm"
          />
          <input
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder="Your email address"
            required
            className="bg-navy-dark border border-sky-blue/30 rounded-lg px-4 py-2.5 text-cream placeholder-sky-blue/50 focus:outline-none focus:border-neon-red transition-colors text-sm"
          />
        </div>
        {status === 'error' && <p className="text-neon-red text-sm">{message}</p>}
        <button
          type="submit"
          disabled={status === 'loading'}
          className="w-full btn-neon py-3 disabled:opacity-50"
        >
          {status === 'loading' ? 'Subscribing...' : 'Subscribe — It\'s free'}
        </button>
        <p className="text-sky-blue/50 text-xs text-center">No spam. Unsubscribe anytime.</p>
      </form>
    </div>
  );
}
